import React, { useState, useCallback } from 'react';
import { TextField, InputAdornment, IconButton, Box, Autocomplete, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useDebounce } from '../../../hooks/utils/useDebounce';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(3),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
      },
    },
  },
}));

/**
 * SearchBox component for standardized search functionality
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Search value
 * @param {function} props.onChange - Change handler
 * @param {function} props.onSearch - Search handler (called on Enter or search button click)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.autoFocus - Auto focus input
 * @param {number} props.debounceMs - Debounce delay in milliseconds
 * @param {boolean} props.showClearButton - Show clear button
 * @param {boolean} props.showFilterButton - Show filter button
 * @param {function} props.onFilterClick - Filter button click handler
 * @param {Array} props.suggestions - Autocomplete suggestions
 * @param {boolean} props.loading - Loading state
 * @param {string} props.size - Input size
 * @param {boolean} props.fullWidth - Full width input
 * @param {Array} props.filters - Active filters
 * @param {function} props.onFilterRemove - Filter remove handler
 */
const SearchBox = ({
  value = '',
  onChange,
  onSearch,
  placeholder = 'Search...',
  autoFocus = false,
  debounceMs = 300,
  showClearButton = true,
  showFilterButton = false,
  onFilterClick,
  suggestions = [],
  loading = false,
  size = 'medium',
  fullWidth = false,
  filters = [],
  onFilterRemove,
  ...otherProps
}) => {
  const [searchValue, setSearchValue] = useState(value);

  // Debounced search to avoid excessive API calls
  const debouncedSearch = useDebounce((searchTerm) => {
    if (onChange) {
      onChange(searchTerm);
    }
  }, debounceMs);

  // Handle input change
  const handleInputChange = useCallback(
    (event, newValue) => {
      const inputValue = typeof newValue === 'string' ? newValue : event?.target?.value || '';
      setSearchValue(inputValue);
      debouncedSearch(inputValue);
    },
    [debouncedSearch]
  );

  // Handle clear button click
  const handleClear = () => {
    setSearchValue('');
    if (onChange) {
      onChange('');
    }
  };

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && onSearch) {
      onSearch(searchValue);
    }
  };

  // Handle search button click
  const handleSearchClick = () => {
    if (onSearch) {
      onSearch(searchValue);
    }
  };

  // Render input adornments
  const startAdornment = (
    <InputAdornment position="start">
      <SearchIcon color="action" />
    </InputAdornment>
  );

  const endAdornment = (
    <InputAdornment position="end">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showClearButton && searchValue && (
          <IconButton size="small" onClick={handleClear} edge="end" aria-label="clear search">
            <ClearIcon />
          </IconButton>
        )}

        {showFilterButton && (
          <IconButton
            size="small"
            onClick={onFilterClick}
            edge="end"
            aria-label="filters"
            color={filters.length > 0 ? 'primary' : 'default'}
          >
            <FilterListIcon />
          </IconButton>
        )}

        {onSearch && (
          <IconButton
            size="small"
            onClick={handleSearchClick}
            edge="end"
            aria-label="search"
            color="primary"
          >
            <SearchIcon />
          </IconButton>
        )}
      </Box>
    </InputAdornment>
  );

  // Autocomplete variant
  if (suggestions.length > 0) {
    return (
      <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
        <Autocomplete
          freeSolo
          options={suggestions}
          value={searchValue}
          onChange={handleInputChange}
          onInputChange={handleInputChange}
          loading={loading}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              placeholder={placeholder}
              size={size}
              fullWidth={fullWidth}
              autoFocus={autoFocus}
              onKeyPress={handleKeyPress}
              InputProps={{
                ...params.InputProps,
                startAdornment: startAdornment,
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {params.InputProps.endAdornment}
                    {endAdornment}
                  </Box>
                ),
              }}
              {...otherProps}
            />
          )}
        />

        {/* Active filters */}
        {filters.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {filters.map((filter, index) => (
              <Chip
                key={index}
                label={filter.label || filter}
                size="small"
                onDelete={onFilterRemove ? () => onFilterRemove(filter) : undefined}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Regular text field variant
  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <StyledTextField
        value={searchValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        size={size}
        fullWidth={fullWidth}
        autoFocus={autoFocus}
        onKeyPress={handleKeyPress}
        InputProps={{
          startAdornment: startAdornment,
          endAdornment: endAdornment,
        }}
        {...otherProps}
      />

      {/* Active filters */}
      {filters.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {filters.map((filter, index) => (
            <Chip
              key={index}
              label={filter.label || filter}
              size="small"
              onDelete={onFilterRemove ? () => onFilterRemove(filter) : undefined}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

SearchBox.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  debounceMs: PropTypes.number,
  showClearButton: PropTypes.bool,
  showFilterButton: PropTypes.bool,
  onFilterClick: PropTypes.func,
  suggestions: PropTypes.array,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  fullWidth: PropTypes.bool,
  filters: PropTypes.array,
  onFilterRemove: PropTypes.func,
};

export default SearchBox;
